// GoogleMapsManager.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { Loader } from '@googlemaps/js-api-loader';

// Create a context to share Google Maps loading state across components
const GoogleMapsContext = createContext(null);

// Libraries that might be needed by various components
const ALL_LIBRARIES = ["places", "maps"];

// Single instance of the Loader to prevent multiple scripts loading
const loader = new Loader({
  id: "__googleMapsScriptId",
  nonce: "caffe67d7b989af3a1c7f4a1a6c79bd9fb2b4eb0",
  apiKey: "AIzaSyAJf2dFo6JBXahwKdm-sLd_3TWg5xaKD7Y",
  libraries: ALL_LIBRARIES,
  language: "es",
  region: "MX",
  version: "weekly",
  retries: 3,
});

// Statuses for Google Maps loading
const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  LOADED: "loaded",
  ERROR: "error"
};

export const GoogleMapsProvider = ({ children }) => {
  const [status, setStatus] = useState(STATUS.IDLE);
  const loadCallbacksRef = useRef([]);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 10;
  const RETRY_DELAY = 500; // ms
  
  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);
  
  // Check if all required Google Maps components are loaded
  const verifyModulesLoaded = () => {
    if (window.google && 
        window.google.maps && 
        window.google.maps.places && 
        window.google.maps.places.Autocomplete) {
      
      console.log("✅ Google Maps API and modules loaded successfully");
      setStatus(STATUS.LOADED);
      
      // Notify all waiting callbacks
      loadCallbacksRef.current.forEach(callback => callback());
      loadCallbacksRef.current = [];
      
      return true;
    }
    
    console.warn("⚠️ Google Maps API check:", {
      googleExists: !!window.google,
      mapsExists: !!(window.google && window.google.maps),
      placesExists: !!(window.google && window.google.maps && window.google.maps.places),
      autocompleteExists: !!(window.google && window.google.maps && 
                          window.google.maps.places && window.google.maps.places.Autocomplete)
    });
    
    return false;
  };
  
  const retryVerification = () => {
    if (retryCountRef.current >= MAX_RETRIES) {
      console.error(`❌ Failed to load Google Maps modules after ${MAX_RETRIES} attempts`);
      setStatus(STATUS.ERROR);
      return;
    }
    
    retryCountRef.current++;
    console.log(`🔄 Verifying Google Maps module (${retryCountRef.current}/${MAX_RETRIES})...`);
    
    if (verifyModulesLoaded()) {
      return; // Success!
    }
    
    // Schedule next retry
    retryTimerRef.current = setTimeout(retryVerification, RETRY_DELAY);
  };
  
  // Function to load Google Maps
  const loadGoogleMaps = () => {
    // Don't reload if already loading or loaded
    if (status === STATUS.LOADING) {
      return new Promise(resolve => {
        loadCallbacksRef.current.push(resolve);
      });
    }
    
    if (status === STATUS.LOADED && verifyModulesLoaded()) {
      return Promise.resolve();
    }
    
    console.log("🔄 Loading Google Maps API...");
    setStatus(STATUS.LOADING);
    retryCountRef.current = 0;
    
    // Clear any existing retry timers
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }
    
    return loader.loadPromise()
      .then(() => {
        console.log("📡 Google Maps script loaded, verifying modules...");
        retryVerification();
        return new Promise(resolve => {
          loadCallbacksRef.current.push(resolve);
        });
      })
      .catch(error => {
        console.error("❌ Error loading Google Maps API:", error);
        setStatus(STATUS.ERROR);
        return Promise.reject(error);
      });
  };
  
  // Load Google Maps as soon as this provider mounts
  useEffect(() => {
    if (status === STATUS.IDLE) {
      loadGoogleMaps();
    }
  }, []);
  
  return (
    <GoogleMapsContext.Provider value={{ status, loadGoogleMaps }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

// Hook to use Google Maps
export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  
  if (!context) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }
  
  return context;
};

// Component that loads Google Maps when needed
export const LoadGoogleMapsScript = ({ children }) => {
  const { status, loadGoogleMaps } = useGoogleMaps();
  
  useEffect(() => {
    if (status === STATUS.IDLE || status === STATUS.ERROR) {
      loadGoogleMaps();
    }
  }, [status, loadGoogleMaps]);
  
  return <>{children}</>;
};